// Debt Service - Updated January 10, 2025: Added wallet debt integration functionality
// Enhanced with expandable loan details section for comprehensive loan information display
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CreditCard as CreditCardIcon,
  School as EducationIcon,
  Home as HomeIcon,
  DirectionsCar as AutoIcon,
  Business as BusinessIcon,
  MoreHoriz as OtherIcon,
  Payment as PaymentIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as BankIcon,
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
  ContactPhone as ContactPhoneIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { formatCurrency, formatDate, formatPercentage } from '../../../utils/formatters';
import { createDebt, updateDebt, deleteDebt, recordDebtPayment } from '../services/cashFlowService';
import debtService from '../../../services/debtService';
import DebtStatsDashboard from './DebtStatsDashboard';

// Enhanced debt type configuration with colors and gradients
const DEBT_TYPE_CONFIG = {
  'Credit Card': {
    icon: CreditCardIcon,
    color: 'from-red-500 to-pink-600',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    lightColor: 'bg-red-100',
    emoji: '💳'
  },
  'Student Loan': {
    icon: EducationIcon,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    lightColor: 'bg-blue-100',
    emoji: '🎓'
  },
  'Mortgage': {
    icon: HomeIcon,
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    lightColor: 'bg-green-100',
    emoji: '🏠'
  },
  'Auto Loan': {
    icon: AutoIcon,
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    lightColor: 'bg-orange-100',
    emoji: '🚗'
  },
  'Business Loan': {
    icon: BusinessIcon,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    lightColor: 'bg-purple-100',
    emoji: '🏢'
  },
  'Other': {
    icon: OtherIcon,
    color: 'from-gray-500 to-slate-600',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    lightColor: 'bg-gray-100',
    emoji: '📄'
  }
};

const DEFAULT_DEBT_CONFIG = DEBT_TYPE_CONFIG['Other'];

const PAYMENT_STRATEGIES = [
  { value: 'avalanche', label: 'Avalanche (Highest Interest First)' },
  { value: 'snowball', label: 'Snowball (Smallest Balance First)' },
  { value: 'custom', label: 'Custom' }
];

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

/**
 * Enhanced Debt Card Component with beautiful design, expandable details, and inline editing
 */
const EnhancedDebtCard = ({ debt, onEdit, onDelete, onRecordPayment, isOnlyDebt = false }) => {
  const [expanded, setExpanded] = useState(isOnlyDebt); // Auto-expand if it's the only debt
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  const debtConfig = DEBT_TYPE_CONFIG[debt.type] || DEFAULT_DEBT_CONFIG;
  const DebtIcon = debtConfig.icon;

  // Initialize edit form data
  const initializeEditForm = () => {
    // Handle dueDate - could be a number (day of month) or date string
    let dueDateForForm = '';
    if (debt.dueDate) {
      if (typeof debt.dueDate === 'number') {
        // If it's a number (day of month), create a date string for current month
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(debt.dueDate).padStart(2, '0');
        dueDateForForm = `${year}-${month}-${day}`;
      } else if (typeof debt.dueDate === 'string') {
        // If it's already a string, try to extract date part
        dueDateForForm = debt.dueDate.includes('T') ? debt.dueDate.split('T')[0] : debt.dueDate;
      } else {
        // If it's a Date object
        dueDateForForm = new Date(debt.dueDate).toISOString().split('T')[0];
      }
    }

    // Handle targetPayoffDate
    let targetPayoffDateForForm = '';
    if (debt.targetPayoffDate) {
      if (typeof debt.targetPayoffDate === 'string') {
        targetPayoffDateForForm = debt.targetPayoffDate.includes('T') ? debt.targetPayoffDate.split('T')[0] : debt.targetPayoffDate;
      } else if (debt.targetPayoffDate instanceof Date) {
        targetPayoffDateForForm = debt.targetPayoffDate.toISOString().split('T')[0];
      }
    }

    setEditFormData({
      name: debt.name || '',
      type: debt.type || '',
      creditor: debt.creditor || '',
      currentBalance: debt.currentBalance || '',
      originalAmount: debt.originalAmount || debt.currentBalance || '',
      interestRate: debt.interestRate || '',
      minimumPayment: debt.minimumPayment || debt.monthlyPayment || '',
      dueDate: dueDateForForm,
      state: debt.state || '',
      status: debt.status || 'active',
      lastFourDigits: debt.lastFourDigits || '',
      
      // Loan details
      loanTerm: debt.loanDetails?.termLength || '',
      paymentFrequency: debt.loanDetails?.paymentFrequency || 'monthly',
      
      // Contact information
      contactPhone: debt.contactInfo?.phone || '',
      contactEmail: debt.contactInfo?.email || '',
      
      // Strategy
      paymentStrategy: debt.paymentStrategy || 'minimum',
      targetPayoffDate: targetPayoffDateForForm,
      
      notes: debt.notes || ''
    });
  };

  // Handle edit form field changes
  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate interest costs when balance or rate changes
    if (field === 'currentBalance' || field === 'interestRate' || field === 'minimumPayment') {
      const balance = parseFloat(field === 'currentBalance' ? value : editFormData.currentBalance) || 0;
      const rate = parseFloat(field === 'interestRate' ? value : editFormData.interestRate) || 0;
      const payment = parseFloat(field === 'minimumPayment' ? value : editFormData.minimumPayment) || 0;
      
      // Calculate payoff time (simplified calculation)
      if (balance > 0 && rate > 0 && payment > 0) {
        const monthlyRate = rate / 100 / 12;
        const monthsToPayoff = -Math.log(1 - (balance * monthlyRate) / payment) / Math.log(1 + monthlyRate);
        
        if (isFinite(monthsToPayoff) && monthsToPayoff > 0) {
          const payoffDate = new Date();
          payoffDate.setMonth(payoffDate.getMonth() + Math.ceil(monthsToPayoff));
          setEditFormData(prev => ({
            ...prev,
            calculatedPayoffDate: payoffDate.toISOString().split('T')[0]
          }));
        }
      }
    }
  };

  const handleEditClick = () => {
    initializeEditForm();
    setIsEditMode(true);
    setExpanded(true); // Auto-expand when editing
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
  };

  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare update data
      const updateData = {
        name: editFormData.name,
        type: editFormData.type,
        creditor: editFormData.creditor,
        currentBalance: parseFloat(editFormData.currentBalance) || 0,
        originalAmount: parseFloat(editFormData.originalAmount) || 0,
        interestRate: parseFloat(editFormData.interestRate) || 0,
        minimumPayment: parseFloat(editFormData.minimumPayment) || 0,
        dueDate: editFormData.dueDate || null,
        state: editFormData.state,
        status: editFormData.status,
        lastFourDigits: editFormData.lastFourDigits,
        
        loanDetails: {
          termLength: editFormData.loanTerm ? parseInt(editFormData.loanTerm) : null,
          paymentFrequency: editFormData.paymentFrequency,
          paymentAmount: parseFloat(editFormData.minimumPayment) || 0
        },
        
        contactInfo: {
          phone: editFormData.contactPhone,
          email: editFormData.contactEmail
        },
        
        paymentStrategy: editFormData.paymentStrategy,
        targetPayoffDate: editFormData.targetPayoffDate || null,
        notes: editFormData.notes
      };

      // Call the onEdit function with debt ID and update data
      if (typeof onEdit === 'function') {
        await onEdit(debt._id, updateData);
      }
      
      setIsEditMode(false);
      setEditFormData({});
    } catch (error) {
      console.error('Error saving debt:', error);
      // Handle error (show notification, etc.)
    } finally {
      setIsSaving(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateProgress = () => {
    if (!debt.originalAmount || debt.originalAmount <= 0) return 0;
    return ((debt.originalAmount - debt.currentBalance) / debt.originalAmount) * 100;
  };

  const getPaymentAmount = () => {
    return debt.minimumPayment || debt.monthlyPayment || debt.loanDetails?.paymentAmount || 0;
  };

  const getProgressColor = () => {
    const progress = calculateProgress();
    if (progress > 75) return '#10b981'; // green
    if (progress > 50) return '#f59e0b'; // amber
    if (progress > 25) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  const getBalanceStatus = () => {
    const balance = debt.currentBalance || 0;
    if (balance === 0) return { label: 'Paid Off', color: 'success' };
    if (balance > 10000) return { label: 'High Balance', color: 'error' };
    if (balance > 5000) return { label: 'Medium Balance', color: 'warning' };
    return { label: 'Low Balance', color: 'info' };
  };

  const balanceStatus = getBalanceStatus();

  return (
    <Card className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 bg-gradient-to-br from-white via-white to-gray-50/50 backdrop-blur-sm">
      <CardContent className="p-0">
        {/* Header Section */}
        <div className={`bg-gradient-to-r ${debtConfig.color} p-4 sm:p-6 text-white`}>
          <div className="flex flex-col items-center">
            {isEditMode ? (
              <div className="space-y-2 w-full">
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => handleEditFieldChange('name', e.target.value)}
                  className="w-full px-3 py-2 text-base sm:text-lg font-bold bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                  placeholder="Debt name"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={editFormData.creditor || ''}
                    onChange={(e) => handleEditFieldChange('creditor', e.target.value)}
                    className="px-3 py-1 text-xs sm:text-sm bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                    placeholder="Creditor"
                  />
                  <input
                    type="text"
                    value={editFormData.lastFourDigits || ''}
                    onChange={(e) => handleEditFieldChange('lastFourDigits', e.target.value)}
                    className="px-3 py-1 text-xs sm:text-sm bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                    placeholder="Last 4 digits"
                    maxLength="4"
                  />
                </div>
                <div className="flex space-x-2 justify-center">
                  <button
                    onClick={handleEditSave}
                    disabled={isSaving}
                    className="px-3 py-1 bg-green-600 text-white border border-green-700 rounded hover:bg-green-700 transition-colors text-xs sm:text-sm disabled:opacity-50"
                    title="Save changes"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleEditCancel}
                    className="px-3 py-1 bg-white/20 text-white border border-white/30 rounded hover:bg-white/30 transition-colors text-xs sm:text-sm"
                    title="Cancel editing"
                  >
                    Cancel
                  </button>
                </div>
                <input
                  type="number"
                  value={editFormData.currentBalance || ''}
                  onChange={(e) => handleEditFieldChange('currentBalance', e.target.value)}
                  className="w-full px-3 py-2 text-center font-bold bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                  placeholder="Balance"
                  step="0.01"
                />
              </div>
            ) : (
              <>
                {/* Icon - On its own line at top, centered */}
                <div className="flex justify-center mb-2 sm:mb-3">
                  <div className="p-2 sm:p-2.5 bg-white/20 rounded-lg">
                    <DebtIcon className="text-xl sm:text-2xl text-white" />
                  </div>
                </div>
                
                {/* Value - On its own line, centered */}
                <div className="text-center mb-2 sm:mb-3">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                    {formatCurrency(debt.currentBalance)}
                  </div>
                  <div className="text-xs sm:text-sm text-white/80 mt-1">
                    Current Balance
                  </div>
                </div>
                
                {/* Title and Details - Centered */}
                <div className="text-center mb-2 sm:mb-3">
                  <Typography variant="h6" className="font-bold text-white text-base sm:text-lg md:text-xl">
                    {debt.name}
                  </Typography>
                  <Typography variant="body2" className="text-white/80 text-xs sm:text-sm mt-1">
                    {debt.type} • {debt.creditor}
                    {debt.lastFourDigits && ` • •••${debt.lastFourDigits}`}
                  </Typography>
                </div>
                
                {/* Edit Button - Centered */}
                <div className="mb-2 sm:mb-3">
                  <button
                    onClick={handleEditClick}
                    className="px-3 py-1 bg-white/20 text-white border border-white/30 rounded hover:bg-white/30 transition-colors text-xs sm:text-sm"
                    title="Edit debt"
                  >
                    Edit
                  </button>
                </div>
                
                {/* Status Chips - Centered */}
                <div className="flex flex-wrap gap-2 justify-center mb-2">
                  {debt.isWalletAccount && (
                    <Chip 
                      label="Connected Bank" 
                      size="small" 
                      className="bg-white/20 text-white border-white/30 text-[10px] sm:text-xs" 
                    />
                  )}
                  {debt.status === 'active' && (
                    <Chip 
                      label="Active" 
                      size="small" 
                      className="bg-green-500/30 text-green-100 border-green-300/30 text-[10px] sm:text-xs" 
                    />
                  )}
                  <Chip 
                    label={balanceStatus.label}
                    size="small" 
                    className={`text-[10px] sm:text-xs ${
                      balanceStatus.color === 'success' ? 'bg-green-500/30 text-green-100 border-green-300/30' :
                      balanceStatus.color === 'error' ? 'bg-red-500/30 text-red-100 border-red-300/30' :
                      balanceStatus.color === 'warning' ? 'bg-yellow-500/30 text-yellow-100 border-yellow-300/30' :
                      'bg-blue-500/30 text-blue-100 border-blue-300/30'
                    }`}
                  />
                </div>
                
                {/* Credit Card Information - Centered */}
                {debt.type === 'Credit Card' && (
                  <div className="text-xs text-white/60 mt-1 space-y-1 text-center">
                    {debt.availableBalance > 0 && (
                      <div>Available Credit: {formatCurrency(debt.availableBalance)}</div>
                    )}
                    {debt.totalCreditLimit > 0 && (
                      <div>Total Credit Limit: {formatCurrency(debt.totalCreditLimit)}</div>
                    )}
                    {debt.lastStatementBalance !== undefined && debt.lastStatementBalance !== null && (
                      <div>Last Statement: {formatCurrency(debt.lastStatementBalance)}</div>
                    )}
                    {debt.lastStatementDate && (
                      <div>Statement Date: {new Date(debt.lastStatementDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Quick Stats Section - Mobile optimized */}
        <div className="p-3 sm:p-4 md:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-6">
            <div className={`text-center p-2 sm:p-3 md:p-4 ${debtConfig.bgColor} rounded-xl`}>
              <PaymentIcon className={`${debtConfig.textColor} mx-auto mb-1 sm:mb-2 h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6`} />
              <div className={`text-base sm:text-lg md:text-xl lg:text-2xl font-bold ${debtConfig.textColor}`}>
                {formatCurrency(getPaymentAmount())}
              </div>
              <div className={`text-[10px] sm:text-xs ${debtConfig.textColor.replace('700', '600')}`}>Monthly Payment</div>
            </div>
            
            <div className={`text-center p-2 sm:p-3 md:p-4 ${debtConfig.bgColor} rounded-xl`}>
              <TrendingDownIcon className={`${debtConfig.textColor} mx-auto mb-1 sm:mb-2 h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6`} />
              <div className={`text-base sm:text-lg md:text-xl lg:text-2xl font-bold ${debtConfig.textColor}`}>
                {debt.interestRate ? `${debt.interestRate.toFixed(2)}%` : 'N/A'}
              </div>
              <div className={`text-[10px] sm:text-xs ${debtConfig.textColor.replace('700', '600')}`}>Interest Rate</div>
            </div>
            
            <div className={`text-center p-2 sm:p-3 md:p-4 ${debtConfig.bgColor} rounded-xl col-span-2 lg:col-span-1`}>
              <CheckCircleIcon className={`${debtConfig.textColor} mx-auto mb-1 sm:mb-2 h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6`} />
              <div className={`text-base sm:text-lg md:text-xl lg:text-2xl font-bold ${debtConfig.textColor}`}>
                {debt.type === 'Credit Card' && debt.totalCreditLimit > 0
                  ? `${debt.creditUtilization || 0}%`
                  : `${calculateProgress().toFixed(0)}%`
                }
              </div>
              <div className={`text-[10px] sm:text-xs ${debtConfig.textColor.replace('700', '600')}`}>
                    {debt.type === 'Credit Card' && debt.totalCreditLimit > 0
                      ? 'Credit Utilization' 
                      : 'Progress'
                    }
                  </div>
                </div>
                
                {/* Interest Rate Display - Manual Entry */}
                <div className={`text-center p-3 sm:p-4 ${debtConfig.bgColor} rounded-xl cursor-pointer hover:opacity-80`}
                     onClick={() => setEditingDebt(debt.id)}>
                  <div className={`text-xl sm:text-2xl font-bold ${debtConfig.textColor}`}>
                    {debt.interestRate && debt.interestRate > 0 
                      ? `${debt.interestRate.toFixed(2)}%` 
                      : 'Click to set'
                    }
                  </div>
                  <div className={`text-xs ${debtConfig.textColor.replace('700', '600')}`}>
                    Interest Rate {!debt.interestRate && '(Tap to add)'}
                  </div>
                </div>
                
                {/* Due Date Display - Manual Entry */}
                <div className={`text-center p-3 sm:p-4 ${debtConfig.bgColor} rounded-xl cursor-pointer hover:opacity-80`}
                     onClick={() => setEditingDebt(debt.id)}>
                  <div className={`text-xl sm:text-2xl font-bold ${debtConfig.textColor}`}>
                    {debt.nextPaymentDue 
                      ? new Date(debt.nextPaymentDue).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : 'Click to set'
                    }
                  </div>
                  <div className={`text-xs ${debtConfig.textColor.replace('700', '600')}`}>
                    Next Due Date {!debt.nextPaymentDue && '(Tap to add)'}
                  </div>
            </div>
          </div>

          {/* Progress Bar */}
          {debt.originalAmount && debt.originalAmount > 0 && (
            <div className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between mb-2 space-y-1 sm:space-y-0">
                <span className="text-sm font-medium text-gray-700">Debt Paydown Progress</span>
                <span className="text-sm text-gray-600">
                  {formatCurrency(debt.originalAmount - debt.currentBalance)} paid off
                </span>
              </div>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(calculateProgress(), 100)}
                className="h-3 rounded-full"
                sx={{
                  backgroundColor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getProgressColor(),
                    borderRadius: '9999px'
                  }
                }}
              />
            </div>
          )}

          {/* Expand Button and Actions - Mobile optimized */}
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            <Button
              onClick={() => setExpanded(!expanded)}
              startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              className={`${debtConfig.textColor} hover:${debtConfig.bgColor} w-full sm:w-auto`}
              size="small"
            >
              {expanded ? 'Hide Details' : 'View Details'}
            </Button>
            
            {!isEditMode && (
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onEdit(debt)}
                  className={`bg-gradient-to-r ${debtConfig.color} text-white hover:opacity-90 w-full sm:w-auto text-xs sm:text-sm px-2 sm:px-4`}
                >
                  EDIT
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onDelete(debt._id)}
                  className="border-red-500 text-red-500 hover:bg-red-50 w-full sm:w-auto text-xs sm:text-sm px-2 sm:px-4"
                  startIcon={<DeleteIcon />}
                >
                  DELETE
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Expandable Details Section */}
        <Collapse in={expanded}>
          <div className="px-6 pb-6">
            <Divider className="mb-4" />
            
            {isEditMode ? (
              <div className="space-y-4">
                {/* Basic Information Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Debt Type</label>
                      <select
                        value={editFormData.type || ''}
                        onChange={(e) => handleEditFieldChange('type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select Type</option>
                        {Object.keys(DEBT_TYPE_CONFIG).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={editFormData.status || ''}
                        onChange={(e) => handleEditFieldChange('status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Please Select an Option</option>
                        <option value="active">Active</option>
                        <option value="paid-off">Paid Off</option>
                        <option value="consolidated">Consolidated</option>
                        <option value="defaulted">Defaulted</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Original Amount</label>
                      <input
                        type="number"
                        value={editFormData.originalAmount || ''}
                        onChange={(e) => handleEditFieldChange('originalAmount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interest Rate (%)</label>
                      <input
                        type="number"
                        value={editFormData.interestRate || ''}
                        onChange={(e) => handleEditFieldChange('interestRate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="5.25"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Payment Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Payment</label>
                      <input
                        type="number"
                        value={editFormData.minimumPayment || ''}
                        onChange={(e) => handleEditFieldChange('minimumPayment', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Frequency</label>
                      <select
                        value={editFormData.paymentFrequency || ''}
                        onChange={(e) => handleEditFieldChange('paymentFrequency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Please Select an Option</option>
                        <option value="monthly">Monthly</option>
                        <option value="bi-weekly">Bi-weekly</option>
                        <option value="weekly">Weekly</option>
                        <option value="quarterly">Quarterly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={editFormData.dueDate || ''}
                        onChange={(e) => handleEditFieldChange('dueDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Loan Term (months)</label>
                      <input
                        type="number"
                        value={editFormData.loanTerm || ''}
                        onChange={(e) => handleEditFieldChange('loanTerm', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="60"
                      />
                    </div>
                  </div>
                </div>

                {/* Location & Strategy */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Location & Strategy</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        value={editFormData.state || ''}
                        onChange={(e) => handleEditFieldChange('state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select State</option>
                        {US_STATES.map(state => (
                          <option key={state.value} value={state.value}>{state.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Strategy</label>
                      <select
                        value={editFormData.paymentStrategy || ''}
                        onChange={(e) => handleEditFieldChange('paymentStrategy', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Please Select an Option</option>
                        <option value="minimum">Minimum Payment</option>
                        <option value="avalanche">Avalanche (High Interest First)</option>
                        <option value="snowball">Snowball (Low Balance First)</option>
                        <option value="custom">Custom Amount</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Payoff Date</label>
                      <input
                        type="date"
                        value={editFormData.targetPayoffDate || ''}
                        onChange={(e) => handleEditFieldChange('targetPayoffDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    {editFormData.calculatedPayoffDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Calculated Payoff</label>
                        <input
                          type="date"
                          value={editFormData.calculatedPayoffDate}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={editFormData.contactPhone || ''}
                        onChange={(e) => handleEditFieldChange('contactPhone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={editFormData.contactEmail || ''}
                        onChange={(e) => handleEditFieldChange('contactEmail', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="customer.service@creditor.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editFormData.notes || ''}
                    onChange={(e) => handleEditFieldChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Payment history, refinancing options, etc."
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              <>
                <Typography variant="h6" className="text-gray-800 mb-4 font-semibold">
                  Debt Details
                </Typography>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Payment Information */}
                  <div className={`p-4 rounded-xl ${debtConfig.bgColor} border`}>
                    <div className="flex items-center mb-3">
                      <CalendarTodayIcon className={`${debtConfig.textColor} mr-2`} />
                      <Typography variant="subtitle1" className={`font-semibold ${debtConfig.textColor}`}>
                        Payment Schedule
                      </Typography>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Next Due Date:</span>
                        <span className="font-medium">{formatDate(debt.dueDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Frequency:</span>
                        <span className="font-medium">{debt.loanDetails?.paymentFrequency || 'Monthly'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Minimum Payment:</span>
                        <span className="font-bold text-green-600">{formatCurrency(getPaymentAmount())}</span>
                      </div>
                    </div>
                  </div>

                  {/* Account Information */}
                  <div className={`p-4 rounded-xl ${debtConfig.bgColor} border`}>
                    <div className="flex items-center mb-3">
                      <InfoIcon className={`${debtConfig.textColor} mr-2`} />
                      <Typography variant="subtitle1" className={`font-semibold ${debtConfig.textColor}`}>
                        Account Information
                      </Typography>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Interest Rate:</span>
                        <span className="font-medium">{debt.interestRate ? `${debt.interestRate.toFixed(2)}%` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Original Amount:</span>
                        <span className="font-medium">{formatCurrency(debt.originalAmount || debt.currentBalance)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Remaining Term:</span>
                        <span className="font-medium">
                          {debt.loanDetails?.termLength ? `${debt.loanDetails.termLength} months` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Credit Card Statement Information - Only for Credit Cards */}
                  {debt.type === 'Credit Card' && (debt.lastStatementBalance !== undefined || debt.lastStatementDate) && (
                    <div className={`p-4 rounded-xl ${debtConfig.bgColor} border md:col-span-2`}>
                      <div className="flex items-center mb-3">
                        <CreditCardIcon className={`${debtConfig.textColor} mr-2`} />
                        <Typography variant="subtitle1" className={`font-semibold ${debtConfig.textColor}`}>
                          Statement Information
                        </Typography>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Current Statement Balance:</span>
                          <span className="font-bold text-red-600">{formatCurrency(debt.currentBalance)}</span>
                        </div>
                        {debt.lastStatementBalance !== undefined && debt.lastStatementBalance !== null && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Last Statement Balance:</span>
                            <span className="font-medium">{formatCurrency(debt.lastStatementBalance)}</span>
                          </div>
                        )}
                        {debt.lastStatementDate && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Last Statement Date:</span>
                            <span className="font-medium">{new Date(debt.lastStatementDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        )}
                        {debt.nextPaymentDue && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Next Payment Due:</span>
                            <span className="font-medium">{new Date(debt.nextPaymentDue).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {debt.contactInfo && (debt.contactInfo.phone || debt.contactInfo.email) && (
                    <div className={`p-4 rounded-xl ${debtConfig.bgColor} border md:col-span-2`}>
                      <div className="flex items-center mb-3">
                        <ContactPhoneIcon className={`${debtConfig.textColor} mr-2`} />
                        <Typography variant="subtitle1" className={`font-semibold ${debtConfig.textColor}`}>
                          Contact Information
                        </Typography>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {debt.contactInfo.phone && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Phone:</span>
                            <span className="font-medium">{debt.contactInfo.phone}</span>
                          </div>
                        )}
                        {debt.contactInfo.email && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Email:</span>
                            <span className="font-medium">{debt.contactInfo.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {debt.notes && (
                    <div className={`p-4 rounded-xl ${debtConfig.bgColor} border md:col-span-2`}>
                      <div className="flex items-center mb-3">
                        <NoteIcon className={`${debtConfig.textColor} mr-2`} />
                        <Typography variant="subtitle1" className={`font-semibold ${debtConfig.textColor}`}>
                          Notes
                        </Typography>
                      </div>
                      <Typography variant="body2" className="text-gray-700 whitespace-pre-wrap">
                        {debt.notes}
                      </Typography>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Collapse>
      </CardContent>
    </Card>
  );
};

/**
 * Enhanced Debt Section Component with modern design
 */
const DebtSection = ({ debts, onDebtsChange, isPartnerMode = false, partnerName = '' }) => {
  const [editingId, setEditingId] = useState(null);
  const [newDebt, setNewDebt] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localDebts, setLocalDebts] = useState([]);

  // Use local debts if available, otherwise use debts from props
  const effectiveDebts = localDebts.length > 0 ? localDebts : debts;
  const safeDebts = Array.isArray(effectiveDebts) ? effectiveDebts.filter(debt => debt && debt._id) : [];

  // Use debts data from props when in partner mode or when available
  useEffect(() => {
    if (isPartnerMode && debts && debts.length > 0) {
      console.log('🤝 Partner mode: Using debts data from props:', debts);
      // In partner mode, use the data passed from parent
      // No need to fetch debts, they're already provided
    } else if (!isPartnerMode && (!debts || debts.length === 0)) {
      // Fetch if we don't have debts data OR if debts array is empty
      console.log('🏦 DebtSection: No debts data, fetching from API...');
      fetchDebts();
    }
  }, [isPartnerMode]); // Remove debts from dependencies to prevent infinite loop

  // Fetch debts including wallet accounts
  const fetchDebts = async () => {
    try {
      console.log('🏦 DebtSection: Fetching debts and wallet accounts...');
      
      // Clear cache to ensure fresh data
      debtService.clearCache();
      
      const [manualDebts, walletDebts] = await Promise.all([
        debtService.getAllDebts(),
        debtService.getWalletDebtAccounts()
      ]);
      
      console.log('🏦 DebtSection: Manual debts:', manualDebts?.length || 0);
      console.log('🏦 DebtSection: Wallet debts:', walletDebts?.length || 0);
      
      const allDebts = Array.isArray(manualDebts) ? [...manualDebts] : [];
      
      if (Array.isArray(walletDebts)) {
        walletDebts.forEach(walletDebt => {
          const isDuplicate = allDebts.some(debt => 
            debt._id === walletDebt._id || 
            (debt.name === walletDebt.name && debt.type === walletDebt.accountType?.toLowerCase())
          );
          
          if (!isDuplicate) {
            // Use proper balance field for credit cards with Chase anomaly detection
            const isCredit = walletDebt.accountType === 'Credit';
            
            let actualDebt;
            if (isCredit) {
              // **CHASE ANOMALY DETECTION & FIX**
              // Chase sometimes reports currentBalance = availableBalance when card is actually paid off
              const hasChaseAnomaly = (
                walletDebt.currentBalance === walletDebt.availableBalance && 
                walletDebt.currentBalance > 0 &&
                walletDebt.currentBalance >= 1000 // Likely credit limit, not debt
              );
              
              if (hasChaseAnomaly) {
                console.log(`🔧 DebtSection - CHASE ANOMALY DETECTED:`, {
                  account: walletDebt.name || 'CREDIT CARD',
                  mask: walletDebt.lastFourDigits,
                  reportedBalance: walletDebt.currentBalance,
                  availableBalance: walletDebt.availableBalance,
                  issue: 'currentBalance = availableBalance (likely credit limit misreported as debt)',
                  action: 'Treating as $0 debt - card appears paid off'
                });
                
                // For Chase anomalies, assume card is paid off
                actualDebt = 0;
              } else {
                // Normal credit card logic - use currentBalance as debt amount
                actualDebt = walletDebt.currentBalance || 0;
              }
              
              console.log(`🏦 DebtSection: Credit card ${walletDebt.name} balance (debt): ${actualDebt}`, 
                hasChaseAnomaly ? '(Chase anomaly corrected)' : `(availableBalance ignored: ${walletDebt.availableBalance})`);
            } else {
              // For loans, use balance or currentBalance
              actualDebt = walletDebt.balance || walletDebt.currentBalance || 0;
            }
            
            // Calculate credit limit and utilization for display
            const availableCredit = walletDebt.availableBalance || 0;
            const totalCreditLimit = walletDebt.totalCreditLimit || (actualDebt + availableCredit);
            const creditUtilization = actualDebt > 0 && totalCreditLimit > 0 ? 
              ((actualDebt / totalCreditLimit) * 100).toFixed(1) : 0;
            
            const transformedDebt = {
              _id: walletDebt._id,
              name: walletDebt.name,
              type: walletDebt.accountType === 'Credit' ? 'Credit Card' : 
                    walletDebt.accountType === 'Loan' ? 'Auto Loan' : 'Other',
              creditor: walletDebt.institutionName || 'Connected Bank',
              currentBalance: actualDebt,
              availableBalance: availableCredit, // Available credit (remaining)
              totalCreditLimit: totalCreditLimit, // Total credit limit (debt + available)
              creditUtilization: parseFloat(creditUtilization), // Utilization percentage
              interestRate: walletDebt.interestRate || 0,
              minimumPayment: walletDebt.minimumPayment || walletDebt.monthlyPayment || 0,
              monthlyPayment: walletDebt.monthlyPayment || walletDebt.minimumPayment || 0,
              nextPaymentDue: walletDebt.nextPaymentDue || null,
              lastPaymentAmount: walletDebt.lastPaymentAmount || null,
              lastPaymentDate: walletDebt.lastPaymentDate || null,
              isOverdue: walletDebt.isOverdue || false,
              status: 'active',
              isWalletAccount: true,
              lastFourDigits: walletDebt.lastFourDigits || '0000',
              walletData: walletDebt
            };
            console.log('🏦 DebtSection: Adding wallet debt:', transformedDebt.name, 
              `Debt: $${actualDebt}, Available Credit: $${availableCredit}, Total Limit: $${totalCreditLimit}, Utilization: ${creditUtilization}%`);
            // Only add debts with actual balance > 0 to avoid showing $0 debts
            if (actualDebt > 0) {
              allDebts.push(transformedDebt);
            } else {
              console.log('🏦 DebtSection: Skipping debt with $0 balance:', transformedDebt.name);
            }
          }
        });
      }
      
      console.log('🏦 DebtSection: Total debts to display:', allDebts.length);
      setLocalDebts(allDebts);
      onDebtsChange(allDebts);
    } catch (error) {
      console.error('Error fetching debts:', error);
      onDebtsChange([]);
    }
  };

  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    creditor: '',
    originalAmount: '',
    currentBalance: '',
    interestRate: '',
    minimumPayment: '',
    dueDate: '',
    startDate: '',
    endDate: '',
    status: 'active',
    priority: 'medium',
    notes: '',
    paymentStrategy: 'avalanche'
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    status: 'completed',
    notes: ''
  });

  // Filter debts based on active tab
  const filteredDebts = safeDebts.filter(debt => {
    switch (activeTab) {
      case 0: return debt.status === 'active' || !debt.status;
      case 1: return debt.status === 'paid';
      case 2: return true;
      default: return true;
    }
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (debt = null) => {
    if (debt) {
      // Handle dueDate - could be a number (day of month) or date string
      let dueDateForForm = '';
      if (debt.dueDate) {
        if (typeof debt.dueDate === 'number') {
          // If it's a number (day of month), create a date string for current month
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(debt.dueDate).padStart(2, '0');
          dueDateForForm = `${year}-${month}-${day}`;
        } else if (typeof debt.dueDate === 'string') {
          // If it's already a string, try to extract date part
          dueDateForForm = debt.dueDate.includes('T') ? debt.dueDate.split('T')[0] : debt.dueDate;
        } else {
          // If it's a Date object
          dueDateForForm = new Date(debt.dueDate).toISOString().split('T')[0];
        }
      }

      setFormData({
        ...debt,
        dueDate: dueDateForForm,
        startDate: debt.startDate ? new Date(debt.startDate).toISOString().split('T')[0] : '',
        endDate: debt.endDate ? new Date(debt.endDate).toISOString().split('T')[0] : ''
      });
      setSelectedDebt(debt);
    } else {
      setFormData({
        name: '',
        type: '',
        creditor: '',
        originalAmount: '',
        currentBalance: '',
        interestRate: '',
        minimumPayment: '',
        dueDate: '',
        startDate: '',
        endDate: '',
        status: 'active',
        priority: 'medium',
        notes: '',
        paymentStrategy: 'avalanche'
      });
      setSelectedDebt(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDebt(null);
  };

  const handleOpenPaymentDialog = (debt) => {
    setSelectedDebt(debt);
    setPaymentData({
      amount: debt.minimumPayment || '',
      date: new Date().toISOString().split('T')[0],
      status: 'completed',
      notes: ''
    });
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedDebt(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData({
      ...paymentData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const debtData = {
        ...formData,
        originalAmount: parseFloat(formData.originalAmount) || 0,
        currentBalance: parseFloat(formData.currentBalance) || 0,
        interestRate: parseFloat(formData.interestRate) || 0,
        minimumPayment: parseFloat(formData.minimumPayment) || 0
      };

      console.log('💳 Submitting debt data:', debtData);

      let result;
      if (selectedDebt) {
        // Updating existing debt
        console.log('💳 Updating existing debt:', selectedDebt._id);
        result = await updateDebt(selectedDebt._id, debtData);
        console.log('✅ Debt updated successfully:', result);
      } else {
        // Creating new debt
        console.log('💳 Creating new debt');
        result = await createDebt(debtData);
        console.log('✅ Debt created successfully:', result);
      }

      // Clear debt service cache to force fresh data
      debtService.clearCache();
      
      // Refresh debts from server to ensure we have the latest data
      try {
        console.log('🔄 Refreshing debts from server...');
        await fetchDebts(); // This will update localDebts and call onDebtsChange
        console.log('✅ Debts refreshed successfully');
      } catch (refreshError) {
        console.error('❌ Error refreshing debts, using fallback:', refreshError);
        // Fallback to local state update if refresh fails
        if (selectedDebt) {
          onDebtsChange(safeDebts.map(d => d._id === selectedDebt._id ? result : d));
        } else {
          onDebtsChange([...safeDebts, result]);
        }
      }

      handleCloseDialog();
      
      // Show success message (you can replace this with a toast notification)
      console.log('✅ Debt saved successfully!');
      
    } catch (error) {
      console.error('❌ Error saving debt:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Show error message to user (you can replace this with a toast notification)
      alert(`Error ${selectedDebt ? 'updating' : 'creating'} debt: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const payment = {
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      };

      const updatedDebt = await recordDebtPayment(selectedDebt._id, payment);
      onDebtsChange(safeDebts.map(d => d._id === selectedDebt._id ? updatedDebt : d));
      handleClosePaymentDialog();
    } catch (error) {
      console.error('Error recording payment:', error);
    }
  };

  const handleDelete = async (id) => {
    const debtToDelete = safeDebts.find(d => d._id === id);
    const debtName = debtToDelete ? debtToDelete.name : 'this debt';
    
    if (window.confirm(`Are you sure you want to delete "${debtName}"? This action cannot be undone.`)) {
      try {
        console.log('💳 Deleting debt:', id);
        await deleteDebt(id);
        
        // Clear cache to ensure fresh data
        debtService.clearCache();
        
        // Refresh data from server to ensure consistency
        try {
          await fetchDebts();
          console.log('✅ Debt deleted and data refreshed successfully');
        } catch (refreshError) {
          console.error('❌ Error refreshing after delete, using fallback:', refreshError);
          // Fallback to local state update if refresh fails
          onDebtsChange(safeDebts.filter(d => d._id !== id));
        }
        
      } catch (error) {
        console.error('❌ Error deleting debt:', error);
        alert(`Error deleting debt: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-red-50 rounded-xl shadow-lg p-4 sm:p-6">
      {/* Enhanced Header with Stats Dashboard */}
      <div className="mb-6 sm:mb-8">
        <DebtStatsDashboard />
      </div>
      
      {/* Mobile-friendly header layout */}
      <div className="mb-6 sm:mb-8">
        {/* Title and description */}
        <div className="mb-4">
          <Typography variant="h4" className="font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent text-2xl sm:text-3xl lg:text-4xl">
            Debt Management
          </Typography>
          <Typography variant="body1" className="text-gray-600 mt-1 text-sm sm:text-base">
            Track and manage your debts with detailed payment strategies and progress monitoring
          </Typography>
        </div>
        
        {/* Add Debt button on its own line for mobile */}
        <div className="flex justify-center sm:justify-end">
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all duration-300 w-full sm:w-auto"
          >
            Add Debt
          </Button>
        </div>
      </div>

      {/* Enhanced Tabs - Mobile optimized */}
      <Paper className="mb-6 bg-gradient-to-r from-gray-50 to-gray-100 border-0 shadow-md">
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          className="p-1 sm:p-2"
          sx={{
            '& .MuiTab-root': {
              borderRadius: '8px',
              margin: '0 2px sm:0 8px',
              fontSize: '0.75rem',
              minWidth: 'auto',
              padding: '6px 8px',
              '@media (min-width: 640px)': {
                fontSize: '0.875rem',
                padding: '12px 16px',
                minWidth: '160px'
              },
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '@media (min-width: 640px)': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }
              }
            }
          }}
        >
          <Tab label={`Active (${safeDebts.filter(d => d.status === 'active' || !d.status).length})`} />
          <Tab label={`Paid (${safeDebts.filter(d => d.status === 'paid').length})`} />
          <Tab label={`All (${safeDebts.length})`} />
        </Tabs>
      </Paper>

      {filteredDebts.length === 0 ? (
        <Card className="bg-gradient-to-br from-red-50 via-white to-pink-50 border-2 border-dashed border-red-200">
          <CardContent className="text-center p-12">
            <div className="bg-gradient-to-br from-red-100 to-pink-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CreditCardIcon className="text-4xl text-red-600" />
            </div>
            <Typography variant="h5" className="font-bold text-gray-800 mb-3">
              No {activeTab === 0 ? 'active' : activeTab === 1 ? 'paid' : ''} debts found
            </Typography>
            <Typography variant="body1" className="text-gray-600 mb-6 max-w-md mx-auto">
              {activeTab === 0 ? 
                'Start tracking your debts to create effective payoff strategies and monitor your progress.' :
                activeTab === 1 ?
                'Once you pay off debts, they\'ll appear here for your records.' :
                'Add your first debt to begin comprehensive debt management.'
              }
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Add Your First Debt
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {filteredDebts.map((debt) => (
            <EnhancedDebtCard 
              key={debt._id} 
              debt={debt} 
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
              onRecordPayment={handleOpenPaymentDialog}
              isOnlyDebt={filteredDebts.length === 1}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Debt Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedDebt ? 'Edit Debt' : 'Add Debt'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Debt Name"
                  fullWidth
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="type"
                  label="Type"
                  fullWidth
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="Credit Card">Credit Card</MenuItem>
                  <MenuItem value="Student Loan">Student Loan</MenuItem>
                  <MenuItem value="Mortgage">Mortgage</MenuItem>
                  <MenuItem value="Auto Loan">Auto Loan</MenuItem>
                  <MenuItem value="Business Loan">Business Loan</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="creditor"
                  label="Creditor"
                  fullWidth
                  value={formData.creditor}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="originalAmount"
                  label="Original Amount"
                  type="number"
                  fullWidth
                  value={formData.originalAmount}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="currentBalance"
                  label="Current Balance"
                  type="number"
                  fullWidth
                  value={formData.currentBalance}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="interestRate"
                  label="Interest Rate (%)"
                  type="number"
                  fullWidth
                  value={formData.interestRate}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    endAdornment: '%'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="minimumPayment"
                  label="Minimum Payment"
                  type="number"
                  fullWidth
                  value={formData.minimumPayment}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="dueDate"
                  label="Due Date"
                  type="date"
                  fullWidth
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="status"
                  label="Status"
                  fullWidth
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="defaulted">Defaulted</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="priority"
                  label="Priority"
                  fullWidth
                  value={formData.priority}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="paymentStrategy"
                  label="Payment Strategy"
                  fullWidth
                  value={formData.paymentStrategy}
                  onChange={handleInputChange}
                  required
                >
                  {PAYMENT_STRATEGIES.map(strategy => (
                    <MenuItem key={strategy.value} value={strategy.value}>
                      {strategy.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : selectedDebt ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment for {selectedDebt?.name}</DialogTitle>
        <form onSubmit={handlePaymentSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="amount"
                  label="Payment Amount"
                  type="number"
                  fullWidth
                  value={paymentData.amount}
                  onChange={handlePaymentInputChange}
                  required
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="date"
                  label="Payment Date"
                  type="date"
                  fullWidth
                  value={paymentData.date}
                  onChange={handlePaymentInputChange}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="status"
                  label="Status"
                  fullWidth
                  value={paymentData.status}
                  onChange={handlePaymentInputChange}
                  required
                >
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  value={paymentData.notes}
                  onChange={handlePaymentInputChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePaymentDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Record Payment
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default DebtSection; 